// Collection page: the filter drawer and the horizontally scrolling goal chips. Filtering itself is a
// normal page load, so the page also works when this script has not loaded yet.
if (!customElements.get('movitamins-collection')) {
  customElements.define(
    'movitamins-collection',
    class MovitaminsCollection extends HTMLElement {
      connectedCallback() {
        this.drawer = this.querySelector('[data-mvcol-drawer]');
        this.opener = this.querySelector('[data-mvcol-open]');
        this.chips = this.querySelector('[data-mvcol-chips]');
        this.previous = this.querySelector('[data-mvcol-chips-previous]');
        this.next = this.querySelector('[data-mvcol-chips-next]');

        this.opener?.addEventListener('click', () => this.openDrawer());
        this.drawer?.querySelectorAll('[data-mvcol-close]').forEach((button) => button.addEventListener('click', () => this.drawer.close()));
        // A click on the backdrop lands on the dialog element itself.
        this.drawer?.addEventListener('click', (event) => {
          if (event.target === this.drawer) this.drawer.close();
        });
        this.drawer?.addEventListener('close', () => this.opener?.focus());
        // Empty price fields would otherwise end up in the URL as empty filters.
        this.drawer?.querySelector('form')?.addEventListener('submit', (event) => {
          event.currentTarget.querySelectorAll('input[type="number"]').forEach((input) => {
            if (input.value === '') input.disabled = true;
          });
        });

        this.initSeoText();

        if (this.chips) {
          this.onChipsScroll = () => this.updateChipArrows();
          this.chips.addEventListener('scroll', this.onChipsScroll, { passive: true });
          window.addEventListener('resize', this.onChipsScroll);
          this.previous?.addEventListener('click', () => this.scrollChips(-1));
          this.next?.addEventListener('click', () => this.scrollChips(1));
          const active = this.chips.querySelector('.is-active');
          if (active) active.scrollIntoView({ block: 'nearest', inline: 'center' });
          this.updateChipArrows();
        }
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onChipsScroll);
      }

      // Keeps the main heading and first paragraph of the SEO text visible and folds the rest behind
      // "Lees meer". The folded part stays in the page as hidden="until-found", so search engines and the
      // browser's find-in-page still see it. Without this script the whole text is simply shown.
      initSeoText() {
        const section = this.querySelector('[data-mvcol-seo]');
        this.seoToggle = section?.querySelector('[data-mvcol-seo-toggle]');
        if (!section || !this.seoToggle) return;
        const content = section.querySelector('.metafield-rich_text_field') || section.querySelector('.mvcol__seo-body');
        const blocks = [...content.children];
        const firstParagraph = blocks.findIndex((block) => block.tagName === 'P');
        const rest = blocks.slice(firstParagraph + 1);
        if (firstParagraph < 0 || rest.length < 2) return;

        this.seoMore = document.createElement('div');
        this.seoMore.className = 'mvcol__seo-more';
        this.seoMore.id = this.seoToggle.getAttribute('aria-controls');
        this.seoMore.append(...rest);
        content.append(this.seoMore);
        this.seoSection = section;
        this.setSeoExpanded(false);
        this.seoMore.addEventListener('beforematch', () => this.setSeoExpanded(true));
        this.seoToggle.addEventListener('click', () => {
          const expand = this.seoToggle.getAttribute('aria-expanded') !== 'true';
          this.setSeoExpanded(expand);
          // After folding a long text away, bring its start back into view.
          const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mv-sticky-header-offset')) || 0;
          const top = this.seoSection.getBoundingClientRect().top;
          if (!expand && top < header) window.scrollTo({ top: top + window.scrollY - header - 16 });
        });
        this.seoToggle.hidden = false;
      }

      setSeoExpanded(expanded) {
        if (expanded) {
          this.seoMore.removeAttribute('hidden');
        } else {
          this.seoMore.setAttribute('hidden', 'until-found');
        }
        this.seoToggle.setAttribute('aria-expanded', String(expanded));
        this.seoToggle.querySelector('[data-mvcol-seo-label]').textContent = expanded ? 'Lees minder' : 'Lees meer';
      }

      openDrawer() {
        if (!this.drawer) return;
        this.drawer.showModal();
        document.documentElement.classList.add('mvcol-drawer-open');
        this.drawer.addEventListener('close', () => document.documentElement.classList.remove('mvcol-drawer-open'), { once: true });
      }

      scrollChips(direction) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.chips.scrollBy({ left: direction * this.chips.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      updateChipArrows() {
        const max = this.chips.scrollWidth - this.chips.clientWidth;
        const hasLess = this.chips.scrollLeft > 2;
        const hasMore = this.chips.scrollLeft < max - 2;
        if (this.previous) this.previous.hidden = !hasLess;
        if (this.next) this.next.hidden = !hasMore;
        // The wrapper fades the cut-off edge, so it is clear the row scrolls.
        this.chips.parentElement.classList.toggle('has-less', hasLess);
        this.chips.parentElement.classList.toggle('has-more', hasMore);
      }
    }
  );
}
